import { useState } from 'react';
import styled from '@emotion/styled';
import { colors } from '../theme/tokens';
import logoHorizontal from '../assets/logo-horizontal.png';
import { SignOut, House, CookingPot, X, Users, VideoCamera, Chat, Clipboard, Gear } from '@phosphor-icons/react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  isCollapsed: boolean;
  isMobile?: boolean;
  isOpen?: boolean;
}

interface SidebarComponentProps {
  onToggle: (collapsed: boolean) => void;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}


const SidebarContainer = styled.nav<SidebarProps>`
  position: fixed;
  left: 0;
  top: ${props => props.isMobile ? '60px' : '1rem'};
  margin: ${props => props.isMobile ? '0' : '1rem'};
  height: ${props => props.isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 2rem)'};
  width: ${props => props.isCollapsed ? '60px' : '20%'};
  max-width: ${props => props.isCollapsed ? '60px' : '300px'};
  background-color: ${colors.neutral.white};
  border-radius: ${props => props.isMobile ? '0' : '8px'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, width 0.3s ease;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${props => props.isMobile && !props.isOpen ? 'translateX(-100%)' : 'translateX(0)'};

  @media (max-width: 768px) {
    width: 80%;
    max-width: 300px;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
`;

const TopSection = styled.div<SidebarProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.isCollapsed ? '8px' : '1.5rem'};
`;

const LogoContainer = styled.div<SidebarProps>`
  width: ${props => props.isCollapsed ? '0' : '140px'};
  height: 32px;
  overflow: hidden;
  transition: width 0.3s ease;
  opacity: ${props => props.isCollapsed ? '0' : '1'};
`;

const LogoImage = styled.img`
  height: 100%;
  width: auto;
  object-fit: contain;
  object-position: left;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: ${colors.neutral.charcoal};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colors.brand.laurel};
  }
`;

const NavigationSection = styled.div<SidebarProps>`
  padding: ${props => props.isCollapsed ? '8px' : '1.5rem'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.isCollapsed ? '8px' : '12px'};
`;

const NavItemWrapper = styled.div<SidebarProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  color: ${colors.neutral.charcoal};
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s;
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  width: ${props => props.isCollapsed ? '100%' : 'auto'};
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  color: inherit;
  text-decoration: none;
  justify-content: inherit;
  padding: 0.75rem;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
    color: ${colors.brand.darkLaurel};
  }

  &.active {
    background-color: ${colors.brand.laurel};
    color: ${colors.neutral.white};
  }
`;

const NavText = styled.span<SidebarProps>`
  display: ${props => props.isCollapsed ? 'none' : 'inline'};
  font-family: 'Gabarito', sans-serif;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${props => props.isCollapsed ? '0' : '1'};
  transition: opacity 0.2s ease;
`;

const LogoutSection = styled.div<SidebarProps>`
  margin-top: auto;
  padding: ${props => props.isCollapsed ? '8px' : '1.5rem'};
`;

const LogoutButton = styled.button<SidebarProps>`
  width: 100%;
  padding: 0.75rem;
  background: none;
  color: ${colors.reds.bright};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  gap: 8px;
  transition: color 0.2s;

  &:hover {
    color: ${colors.reds.dark};
  }
`;

const LogoutText = styled.span<SidebarProps>`
  display: ${props => props.isCollapsed ? 'none' : 'inline'};
  font-family: 'Gabarito', sans-serif;
  font-size: 16px;
  opacity: ${props => props.isCollapsed ? '0' : '1'};
  transition: opacity 0.2s ease;
`;

const Sidebar = ({ onToggle, isMobile, isOpen, onClose }: SidebarComponentProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleToggle = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onToggle(newCollapsedState);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };


  return (
    <>
      {isMobile && isOpen && <Overlay isOpen={isOpen} onClick={onClose} />}
      <SidebarContainer isCollapsed={isCollapsed} isMobile={isMobile} isOpen={isOpen}>
        <TopSection isCollapsed={isCollapsed}>
          <LogoContainer isCollapsed={isCollapsed}>
            <LogoImage src={logoHorizontal} alt="Logo" />
          </LogoContainer>
          {!isMobile && (
            <ToggleButton onClick={handleToggle}>
              {isCollapsed ? '→' : '←'}
            </ToggleButton>
          )}
          {isMobile && (
            <ToggleButton onClick={onClose}>
              <X size={24} weight="bold" />
            </ToggleButton>
          )}
        </TopSection>

        <NavigationSection isCollapsed={isCollapsed}>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/dashboard" onClick={handleNavClick}>
              <House size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Dashboard</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/recipes" onClick={handleNavClick}>
              <CookingPot size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Recipes</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/meal-plans" onClick={handleNavClick}>
              <Clipboard size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Meal Plans</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/videos" onClick={handleNavClick}>
              <VideoCamera size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Videos</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/community" onClick={handleNavClick}>
              <Chat size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Community</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/users" onClick={handleNavClick}>
              <Users size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Users</NavText>
            </NavItem>
          </NavItemWrapper>
          <NavItemWrapper isCollapsed={isCollapsed}>
            <NavItem to="/settings" onClick={handleNavClick}>
              <Gear size={24} weight="bold" />
              <NavText isCollapsed={isCollapsed}>Settings</NavText>
            </NavItem>
          </NavItemWrapper>
        </NavigationSection>
        
        <LogoutSection isCollapsed={isCollapsed}>
          <LogoutButton isCollapsed={isCollapsed} onClick={handleLogout}>
            <SignOut size={24} weight="bold" />
            <LogoutText isCollapsed={isCollapsed}>Logout</LogoutText>
          </LogoutButton>
        </LogoutSection>
      </SidebarContainer>
    </>
  );
};

export default Sidebar; 